import { toast } from 'react-toastify';
import ShiftService from '../services/ShiftService';

interface UseAutoAssignDeps {
  tableShifts: any[];
  allEmployees: any[];
  setShowAutoAssignConfirm: (v: boolean) => void;
  setIsAssigning: (v: boolean) => void;
  setAssignmentLogs: (v: any) => void;
  addLog: (msg: string) => void;
  setShowClearAllModal: (v: boolean) => void;
  handleToggleAssignment: (shiftId: number, empId: number, isAutoAssign?: boolean) => Promise<void>;
  fetchTableShifts: () => Promise<any>;
}

export function useAutoAssign(deps: UseAutoAssignDeps) {
  const {
    tableShifts, allEmployees,
    setShowAutoAssignConfirm, setIsAssigning, setAssignmentLogs, addLog,
    setShowClearAllModal, handleToggleAssignment, fetchTableShifts,
  } = deps;

  const executeAutoAssign = async () => {
    setShowAutoAssignConfirm(false);
    setIsAssigning(true);
    setAssignmentLogs([]);
    addLog('Starting auto-assignment with extended constraints...');

    const employeesStats = allEmployees.map((e: any) => {
      const weekly = e.contract_hours_per_week || e.weekly_hours || 40;
      const overUnder = e.balance_hours || 0;
      return {
        ...e,
        weekly_hours: weekly,
        balance_hours: overUnder,
        assignedShiftsCount: 0,
        assignedShiftsSumOfHours: 0,
        assignedDays: new Set(),
      };
    });

    const openShifts: any[] = [];
    (tableShifts as any[]).forEach((dayObj: any) => {
      dayObj.shifts?.forEach((shift: any) => {
        const assignedCount = shift.assigned_employees?.length || 0;
        if (assignedCount < shift.employees_needed) {
          openShifts.push({
            shiftId: shift.id,
            workArea: shift.work_area,
            neededCount: shift.employees_needed - assignedCount,
            assignedEmployeeIds: shift.assigned_employees.map((a: any) => a.id),
            startTime: new Date(shift.start_time),
            endTime: new Date(shift.end_time),
          });
        }
      });
    });
    addLog(`Found ${openShifts.length} open shifts...`);
    openShifts.sort((a: any, b: any) => a.startTime - b.startTime);

    const canAssignEmployee = (empStats: any, shift: any) => {
      if (!empStats.work_areas.includes(shift.workArea)) return false;
      const shiftHours = (shift.endTime - shift.startTime) / 3600000;
      const maxAllowed = empStats.weekly_hours * 1.5;
      if (empStats.assignedShiftsSumOfHours + shiftHours > maxAllowed) return false;
      return true;
    };

    let assignmentsMade = 0;
    try {
      const sortEmployeesForRound = (_round: number) => {
        employeesStats.sort((A: any, B: any) => {
          if (A.assignedShiftsCount !== B.assignedShiftsCount) return A.assignedShiftsCount - B.assignedShiftsCount;
          if (A.balance_hours !== B.balance_hours) return A.balance_hours - B.balance_hours;
          return A.id - B.id;
        });
      };

      for (let round = 1; round <= 5; round++) {
        let placedThisRound = 0;
        addLog(`\n=== Round ${round} \u2013 Distribution ===`);
        for (const shift of openShifts) {
          if (shift.neededCount <= 0) continue;
          sortEmployeesForRound(round);
          for (let empStats of employeesStats) {
            if (shift.neededCount <= 0) break;
            if (empStats.assignedShiftsCount >= round) continue;
            if (!canAssignEmployee(empStats, shift)) continue;
            addLog(`Trying Shift #${shift.shiftId} for Employee #${empStats.id}...`);
            try {
              await handleToggleAssignment(shift.shiftId, empStats.id, true);
            } catch (error: any) {
              addLog(`\u26A0 Failed to assign Shift #${shift.shiftId} to Employee #${empStats.id}: ${error.message}`);
              continue;
            }
            await fetchTableShifts();
            shift.neededCount--;
            empStats.assignedShiftsCount++;
            const shiftHours = (shift.endTime - shift.startTime) / 3600000;
            empStats.assignedShiftsSumOfHours += shiftHours;
            assignmentsMade++;
            placedThisRound++;
            addLog(` \u2713 Shift #${shift.shiftId} \u2192 Employee #${empStats.id} assigned (${shift.neededCount} still needed)`);
            await new Promise(res => setTimeout(res, 200));
          }
        }
        addLog(`Round ${round} \u2192 ${placedThisRound} assigned`);
      }
      addLog(`\nAuto-assignment complete. Total assigned: ${assignmentsMade}`);
    } catch (error: any) {
      console.error('AutoAssign =>', error);
      addLog(`\u274C Unexpected error: ${error.message}`);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleClearAllConfirm = async () => {
    try {
      const allAssignments: number[] = [];
      (tableShifts as any[]).forEach((dayObj: any) => {
        dayObj.shifts?.forEach((shift: any) => {
          if (shift.assigned_employees && shift.assigned_employees.length > 0) {
            shift.assigned_employees.forEach((emp: any) => {
              allAssignments.push(emp.assignment_id);
            });
          }
        });
      });
      if (allAssignments.length === 0) {
        toast.info('No assignments to clear.');
        setShowClearAllModal(false);
        return;
      }
      for (const assignmentId of allAssignments) {
        await ShiftService.deleteShiftAssignment(assignmentId);
      }
      await fetchTableShifts();
      setShowClearAllModal(false);
    } catch (err: any) {
      console.error('Error clearing assignments =>', err);
      toast.error('Error clearing assignments: ' + err.message);
    }
  };

  return {
    handleAutoAssignClick: () => setShowAutoAssignConfirm(true),
    executeAutoAssign,
    handleClearAllClick: () => setShowClearAllModal(true),
    handleClearAllConfirm,
  };
}
